/**
 * Variable-aware number formatting for the Medium-Term Forecaster.
 *
 * The backend names output series by label (e.g. "5-Year CDS premium",
 * "Nominal exchange rate per USD", "Local stock market index: Bovespa",
 * "S&P 500", "Local 10-year interest rate (% eop)"). Formatting rules are
 * keyed on those labels so the same series is always printed the same way in
 * the table, the chart tooltip, the drivers panel and the export.
 */

const KIND_RULES = [
  { kind: "cds", test: /cds/i, unit: "bp" },
  { kind: "fx", test: /exchange rate|\bner\b|per usd/i, unit: "" },
  { kind: "rate", test: /rate \(%|interest rate|policy rate|\(% eop\)|\(% yoy|inflation/i, unit: "%" },
  { kind: "index", test: /index|s&p|bovespa|bolsa|\bse\b|ipc|sensex|kospi|jci|ta-?125|moex|ipsa|colcap|bux|psei|bist|top 40/i, unit: "" },
];

/** Classify a series label into cds | fx | rate | index | other. */
export function kindOf(label) {
  const s = String(label || "");
  for (const r of KIND_RULES) if (r.test.test(s)) return r.kind;
  return "other";
}

/** Display unit for a series label ("" when the level is unit-less). */
export function unitOf(label) {
  const k = kindOf(label);
  const r = KIND_RULES.find((x) => x.kind === k);
  return r ? r.unit : "";
}

/**
 * Decimal places for a value of a given series. CDS in bp without decimals
 * (one decimal under 10 bp); FX by magnitude (2–4 decimals); index levels
 * without decimals; rates with two decimals.
 */
export function decimalsFor(label, value) {
  const k = kindOf(label);
  const v = Math.abs(Number(value));
  if (k === "cds") return v < 10 ? 1 : 0;
  if (k === "index") return v >= 100 ? 0 : 2;
  if (k === "rate") return 2;
  if (k === "fx") {
    if (v >= 1000) return 0;
    if (v >= 100) return 2;
    if (v >= 10) return 3;
    return 4;
  }
  return 2;
}

const nfCache = new Map();
function nf(decimals) {
  if (!nfCache.has(decimals)) {
    nfCache.set(
      decimals,
      new Intl.NumberFormat("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    );
  }
  return nfCache.get(decimals);
}

/** Format a level for a series label. Returns "—" for null/NaN. */
export function formatValue(label, value, opts = {}) {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return opts.empty ?? "—";
  const d = opts.decimals ?? decimalsFor(label, n);
  return nf(d).format(n);
}

/** Plain number with fixed decimals and grouping (no series semantics). */
export function formatNumber(value, decimals = 2, opts = {}) {
  const n = Number(value);
  if (value === null || value === undefined || !Number.isFinite(n)) return opts.empty ?? "—";
  return nf(decimals).format(n);
}

/**
 * Relative change as a signed percentage string, e.g. "+1.8 %". `pp: true`
 * prints percentage points ("+0.25 pp"), which is what a change in a rate is.
 */
export function formatChange(value, { pp = false, decimals = 1, empty = "—" } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return empty;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${nf(decimals).format(Math.abs(n))} ${pp ? "pp" : "%"}`;
}

/** Signed level difference in the series' own unit, e.g. "−3.2 bp". */
export function formatDiff(label, value, { empty = "—" } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return empty;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  const d = decimalsFor(label, n);
  const unit = unitOf(label);
  return `${sign}${nf(d).format(Math.abs(n))}${unit ? " " + unit : ""}`;
}

/**
 * Percent change between two levels: `100 * (b / a - 1)`, or `null` when `a`
 * is zero/unusable. For rates (percent units) callers should prefer the
 * percentage-point difference `b - a`.
 */
export function pctChange(a, b) {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y) || x === 0) return null;
  return (y / x - 1) * 100;
}

/**
 * Unit of a consensus input row, read from its label: "(% yoy)" → "%",
 * "$/BBL" → "$/BBL", "USA - S&P500" → "index", "EURUSD" → "rate".
 */
export function inputUnit(label) {
  const s = String(label || "");
  const m = s.match(/\$\/[A-Z]+/);
  if (m) return m[0];
  if (/%/.test(s)) return "%";
  if (/s&p|vix/i.test(s)) return "index pts";
  if (/^[A-Z]{6}$/.test(s.trim())) return "rate";
  return "";
}

/** Whether a consensus input is expressed in percent (so Δ is in pp). */
export function inputIsPercent(label) {
  return /%/.test(String(label || ""));
}
